import { useState, useEffect, useRef, useMemo } from "react";
import { Controller } from "react-hook-form";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import AddIcon from "@mui/icons-material/Add";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import MuiTextField from "@mui/material/TextField";
import CardContainer from "../../../../components/Card/CardContainer";
import CardMedia from "../../../../components/Media/CardMedia";
import SectionHeader from "./SectionHeader";
import {
  SECTION_TYPOGRAPHY_TITLE,
  SECTION_TYPOGRAPHY_SUBTITLE,
  SECTION_TYPOGRAPHY_FONT_FAMILY_LABEL,
  SECTION_TYPOGRAPHY_TITLE_FONT_LABEL,
  SECTION_TYPOGRAPHY_BODY_FONT_LABEL,
  SECTION_TYPOGRAPHY_ADD_TYPOGRAPHY,
  SECTION_TYPOGRAPHY_GENERATING_PREVIEW,
  SECTION_TYPOGRAPHY_ADD_FONT,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import { INPUT_COLOR_BORDER } from "../../../../utils/colors";
import { BRAND_FONTS } from "../../../../utils/constants";
import { useDispatchApp } from "../../../../lib/redux";
import { startGenerateTypographyPreview } from "../../../../actions/adsCampaigns";
import { getReactHookFormNestedError } from "../../../../utils/errors";

const DEFAULT_TYPOGRAPHY_SIZES = {
  heading: "48px",
  subheading: "24px",
  body: "16px",
};

const hasMultiFontFamilies = (typography) =>
  !!typography?.bodyFontFamily?.trim();

const normalizeTypography = (typography, defaults) => ({
  fontFamily: typography?.fontFamily || defaults.fontFamily,
  bodyFontFamily: typography?.bodyFontFamily || "",
  sizes: {
    heading: typography?.sizes?.heading || defaults.sizes.heading,
    body: typography?.sizes?.body || defaults.sizes.body,
  },
});

const areTypographyEqual = (typo1, typo2) => {
  if (!typo1 || !typo2) return false;
  return (
    typo1.fontFamily === typo2.fontFamily &&
    (typo1.bodyFontFamily || "") === (typo2.bodyFontFamily || "") &&
    typo1.sizes?.heading === typo2.sizes?.heading &&
    typo1.sizes?.body === typo2.sizes?.body
  );
};

const isValidTypography = (typography) => !!typography?.fontFamily?.trim();

const buildTypographyForSave = (typography, isMultiFontMode) => {
  const saved = {
    fontFamily: typography.fontFamily,
    sizes: { ...DEFAULT_TYPOGRAPHY_SIZES },
  };

  if (!isMultiFontMode || !hasMultiFontFamilies(typography)) {
    return saved;
  }

  const bodyFontFamily = typography.bodyFontFamily?.trim();

  if (bodyFontFamily) {
    saved.bodyFontFamily = bodyFontFamily;
  }

  return saved;
};

const collectFontsFromTypography = (typography, allFonts) => {
  [typography?.fontFamily, typography?.bodyFontFamily]
    .filter(Boolean)
    .forEach((font) => {
      const exists = BRAND_FONTS.some(
        (f) => f.toLowerCase() === font.toLowerCase()
      );
      if (!exists) {
        allFonts.add(font);
      }
    });
};

const filterFonts = createFilterOptions();

const MAX_FONT_MATCHES_TO_SHOW_ADD_OPTION = 5;

const hasFontMatch = ({ fonts, inputValue }) =>
  fonts.some((font) => font.toLowerCase() === inputValue.toLowerCase());

const getFontValueFromOption = (newValue) => {
  if (!newValue) return "";
  if (typeof newValue === "string") return newValue.trim();
  if (newValue.inputValue) return newValue.inputValue.trim();
  return String(newValue).trim();
};

const FontFamilySelect = ({
  control,
  name,
  labelKey,
  value,
  errors,
  onFontChange,
  t,
  fonts,
  required = false,
}) => {
  const error = getReactHookFormNestedError(errors, name);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography
        variant="body2"
        sx={{
          color: "text.primary",
          fontSize: "14px",
        }}
      >
        {t(labelKey)}
      </Typography>
      <Controller
        control={control}
        name={name}
        defaultValue={value}
        rules={{ required: required ? true : false }}
        render={({ field }) => (
          <Autocomplete
            freeSolo
            options={fonts}
            getOptionLabel={(option) => {
              if (typeof option === "string") return option;
              if (option?.inputValue) return option.inputValue;
              return "";
            }}
            value={field.value || ""}
            onChange={(_, newValue) => {
              const fontValue = getFontValueFromOption(newValue);
              field.onChange(fontValue);
              onFontChange(name, fontValue);
            }}
            onBlur={(e) => {
              const blurValue = e.target.value;
              field.onChange(blurValue);
              onFontChange(name, blurValue);
              const fontValue = e.target.value.trim();
              field.onChange(fontValue);
              onFontChange(fontValue);
            }}
            filterOptions={(options, params) => {
              const filtered = filterFonts(options, params);
              const inputValue = params.inputValue.trim();

              if (
                inputValue &&
                !hasFontMatch({ fonts: options, inputValue }) &&
                filtered.length < MAX_FONT_MATCHES_TO_SHOW_ADD_OPTION
              ) {
                filtered.unshift({
                  inputValue,
                  isAddOption: true,
                });
              }

              return filtered;
            }}
            renderOption={(props, option) => {
              if (option?.isAddOption) {
                const { key, ...optionProps } = props;
                return (
                  <Box
                    component="li"
                    key={key}
                    {...optionProps}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      color: "primary.main",
                    }}
                  >
                    <AddIcon sx={{ fontSize: 18 }} />
                    <Typography variant="body2" component="span">
                      {t(SECTION_TYPOGRAPHY_ADD_FONT, {
                        value: option.inputValue,
                      })}
                    </Typography>
                  </Box>
                );
              }

              const { key, ...optionProps } = props;
              return (
                <li key={key} {...optionProps}>
                  {option}
                </li>
              );
            }}
            isOptionEqualToValue={(option, val) => {
              if (!val) return false;
              if (typeof option === "string") {
                return option.toLowerCase() === val.toLowerCase();
              }
              return false;
            }}
            selectOnFocus
            handleHomeEndKeys
            fullWidth
            size="small"
            renderInput={(params) => (
              <MuiTextField
                {...params}
                variant="filled"
                error={!!error}
                helperText={error?.message}
                inputProps={{
                  ...params.inputProps,
                  onKeyDown: (event) => {
                    params.inputProps.onKeyDown?.(event);
                    if (!event.defaultPrevented && event.key === "Enter") {
                      const inputValue = event.target.value?.trim();
                      if (inputValue) {
                        field.onChange(inputValue);
                        onFontChange(inputValue);
                      }
                    }
                  },
                }}
                sx={{
                  backgroundColor: "fields.background",
                  border: `1px solid ${INPUT_COLOR_BORDER}`,
                  boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                  "& .MuiInputBase-root": {
                    paddingTop: "0px",
                    paddingBottom: "0px",
                    "&:before, &:after": {
                      display: "none",
                    },
                  },
                }}
              />
            )}
          />
        )}
      />
    </Box>
  );
};

const AddTypographyButton = ({ onClick, t }) => (
  <Box
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick();
      }
    }}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      cursor: "pointer",
      color: "text.secondary",
      width: "fit-content",
      "&:hover": {
        opacity: 0.8,
      },
    }}
  >
    <AddIcon sx={{ fontSize: 18 }} />
    <Typography variant="body2" color="text.secondary">
      {t(SECTION_TYPOGRAPHY_ADD_TYPOGRAPHY)}
    </Typography>
  </Box>
);

const TypographyPreview = ({
  previewUrl,
  imageError,
  onImageError,
  isLoading,
  t,
}) => {
  if (isLoading) {
    return (
      <Box
        sx={{
          width: "100%",
          minHeight: 200,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          borderRadius: 2,
          border: `1px solid ${INPUT_COLOR_BORDER}`,
          backgroundColor: "action.hover",
        }}
      >
        <CircularProgress size={40} />
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            textAlign: "center",
            px: 2,
          }}
        >
          {t(SECTION_TYPOGRAPHY_GENERATING_PREVIEW)}
        </Typography>
      </Box>
    );
  }

  if (previewUrl && !imageError) {
    return (
      <Box
        sx={{
          width: "100%",
          minHeight: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 2,
          border: `1px solid ${INPUT_COLOR_BORDER}`,
          overflow: "hidden",
          backgroundColor: "action.hover",
        }}
      >
        <CardMedia
          url={previewUrl}
          onFailedLoadUrl={onImageError}
          sx={{
            width: "100%",
            maxHeight: "100%",
            objectFit: "contain",
          }}
        />
      </Box>
    );
  }

  return null;
};

export const TypographySection = ({ formMethods, hasError = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isMultiFontMode, setIsMultiFontMode] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [editedTypography, setEditedTypography] = useState(null);
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const initialTypographyRef = useRef(null);

  const { control, watch, setValue, errors } = formMethods || {};
  const defaultTypography = useMemo(
    () => ({
      fontFamily: "Inter",
      bodyFontFamily: "",
      sizes: { ...DEFAULT_TYPOGRAPHY_SIZES },
    }),
    []
  );
  const businessTypography = watch?.("businessTypography") || defaultTypography;

  const fonts = useMemo(() => {
    const allFonts = new Set(BRAND_FONTS);
    collectFontsFromTypography(businessTypography, allFonts);
    if (editedTypography) {
      collectFontsFromTypography(editedTypography, allFonts);
    }
    return Array.from(allFonts).sort((a, b) => a.localeCompare(b));
  }, [businessTypography, editedTypography]);

  useEffect(() => {
    if (isEditing) {
      const normalizedTypography = normalizeTypography(
        businessTypography,
        defaultTypography
      );
      setEditedTypography(normalizedTypography);
      initialTypographyRef.current = normalizedTypography;
      setIsMultiFontMode(hasMultiFontFamilies(normalizedTypography));
    } else {
      setEditedTypography(null);
      initialTypographyRef.current = null;
      setIsMultiFontMode(false);
    }
  }, [isEditing, businessTypography, defaultTypography]);

  if (!formMethods || !formMethods.control) {
    return null;
  }

  const businessTypographyPreview = watch("businessTypographyPreview") || "";

  const generatePreview = async (typography) => {
    setIsGeneratingPreview(true);
    setImageError(false);
    setValue("businessTypographyPreview", "", { shouldValidate: false });

    try {
      const response = await dispatch(
        startGenerateTypographyPreview(typography)
      );

      if (response.ok && response.data?.previewUrl) {
        setValue("businessTypographyPreview", response.data.previewUrl, {
          shouldValidate: false,
        });
        setImageError(false);
      } else {
        setImageError(true);
      }
    } catch (error) {
      console.error("Error generating typography preview:", error);
      setImageError(true);
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  const onSave = async () => {
    if (!editedTypography) {
      setIsEditing(false);
      return;
    }

    const typographyToSave = buildTypographyForSave(
      editedTypography,
      isMultiFontMode
    );

    const hasChanges = !areTypographyEqual(
      typographyToSave,
      buildTypographyForSave(
        initialTypographyRef.current,
        hasMultiFontFamilies(initialTypographyRef.current)
      )
    );

    if (!hasChanges) {
      setIsEditing(false);
      return;
    }

    setValue("businessTypography", typographyToSave, { shouldValidate: true });

    if (isValidTypography(typographyToSave)) {
      setIsEditing(false);
      await generatePreview(typographyToSave);
    } else {
      setIsEditing(false);
    }
  };

  const onFontChange = (fieldName, fontFamily) => {
    if (!editedTypography) return;

    const fieldMap = {
      "businessTypography.fontFamily": "fontFamily",
      "businessTypography.bodyFontFamily": "bodyFontFamily",
    };

    const key = fieldMap[fieldName];
    if (!key) return;

    setEditedTypography({
      ...editedTypography,
      [key]: fontFamily,
    });
  };

  const onAddTypography = () => {
    setIsMultiFontMode(true);
  };

  return (
    <CardContainer title="" hasError={hasError}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <SectionHeader
          icon={TextFieldsIcon}
          title={SECTION_TYPOGRAPHY_TITLE}
          subtitle={SECTION_TYPOGRAPHY_SUBTITLE}
          isEditing={isEditing}
          onEdit={() => setIsEditing(true)}
          onSave={onSave}
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {isEditing && editedTypography ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <FontFamilySelect
                control={control}
                name="businessTypography.fontFamily"
                labelKey={
                  isMultiFontMode
                    ? SECTION_TYPOGRAPHY_TITLE_FONT_LABEL
                    : SECTION_TYPOGRAPHY_FONT_FAMILY_LABEL
                }
                value={editedTypography.fontFamily}
                errors={errors}
                onFontChange={onFontChange}
                fonts={fonts}
                t={t}
                required
              />

              {isMultiFontMode && (
                <FontFamilySelect
                  control={control}
                  name="businessTypography.bodyFontFamily"
                  labelKey={SECTION_TYPOGRAPHY_BODY_FONT_LABEL}
                  value={editedTypography.bodyFontFamily}
                  errors={errors}
                  onFontChange={onFontChange}
                  fonts={fonts}
                  t={t}
                />
              )}

              {!isMultiFontMode && (
                <AddTypographyButton onClick={onAddTypography} t={t} />
              )}
            </Box>
          ) : (
            <TypographyPreview
              previewUrl={businessTypographyPreview}
              imageError={imageError}
              onImageError={() => setImageError(true)}
              isLoading={isGeneratingPreview}
              t={t}
            />
          )}
        </Box>
      </Box>
    </CardContainer>
  );
};

export default TypographySection;
